import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Shield, AlertTriangle } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Admin UI for staged feature flags + kill-switch monitoring.
 *
 * Actions:
 *   • Create flag (key, description, initial rollout %)
 *   • Toggle enabled on/off
 *   • Slide rollout % (0-100)
 *   • Toggle admin-only mode
 *   • Toggle auto-kill (whether the monitor is allowed to disable it)
 *
 * Recent audit-log entries are shown below the flag list so an auto-kill
 * is visible immediately when Jana opens the page.
 */

type Flag = {
  key: string;
  description: string | null;
  enabled: boolean;
  rollout_percentage: number;
  enabled_for_admins_only: boolean;
  auto_kill_enabled: boolean;
  launched_at: string | null;
  updated_at: string;
};

type AuditEntry = {
  id: string;
  flag_key: string;
  action: string;
  reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export const Route = createFileRoute("/admin_/feature-flags")({
  ssr: false,
  beforeLoad: requireAdmin,
  component: FeatureFlagsAdmin,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Feature Flags — Admin | Recovery Trauma™" },
    ],
  }),
});

function FeatureFlagsAdmin() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const flagsQ = useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: async (): Promise<Flag[]> => {
      const { data, error } = await supabase
        .from("feature_flags" as never)
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Flag[];
    },
  });

  const auditQ = useQuery({
    queryKey: ["admin", "feature-flags", "audit"],
    queryFn: async (): Promise<AuditEntry[]> => {
      const { data, error } = await supabase
        .from("feature_flag_audit_log" as never)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as AuditEntry[];
    },
    refetchInterval: 30_000,
  });

  const updateFlag = useMutation({
    mutationFn: async (patch: Partial<Flag> & { key: string }) => {
      const { key, ...changes } = patch;
      const { error } = await supabase
        .from("feature_flags" as never)
        .update(changes as never)
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags", "audit"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/dashboard">
            <ArrowLeft className="me-1 h-4 w-4" /> Dashboard
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">Feature flags</h1>
        <div className="flex-1" />
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="me-1 h-4 w-4" /> New flag</Button>
          </DialogTrigger>
          <CreateFlagDialog onClose={() => setCreateOpen(false)} />
        </Dialog>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" /> Kill-switch policy
          </CardTitle>
          <CardDescription>
            Any flag with auto-kill enabled will be turned off automatically when its
            error rate over the last 15 minutes is at least <strong>2× its 24-hour baseline</strong>
            {" "}and there are at least <strong>5 errors</strong> in the window. Monitor runs every minute.
          </CardDescription>
        </CardHeader>
      </Card>

      {flagsQ.isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (flagsQ.data ?? []).length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          No flags yet. Create one to start a staged rollout.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {flagsQ.data!.map((f) => (
            <FlagCard key={f.key} flag={f} onChange={(p) => updateFlag.mutate({ key: f.key, ...p })} />
          ))}
        </div>
      )}

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-base">Recent activity</CardTitle>
          <CardDescription>Last 30 flag changes, including auto-kills.</CardDescription>
        </CardHeader>
        <CardContent>
          {auditQ.data && auditQ.data.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {auditQ.data.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2 border-b border-border/40 pb-2 last:border-0">
                  <Badge variant={a.action === "auto_killed" ? "destructive" : "secondary"}>
                    {a.action.replace(/_/g, " ")}
                  </Badge>
                  <code className="text-xs font-mono">{a.flag_key}</code>
                  {a.reason && <span className="text-muted-foreground">— {a.reason}</span>}
                  <span className="ms-auto text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FlagCard({ flag, onChange }: { flag: Flag; onChange: (p: Partial<Flag>) => void }) {
  const [rollout, setRollout] = useState(flag.rollout_percentage);
  const wasAutoKilled = !flag.enabled && flag.launched_at != null;

  return (
    <Card className={wasAutoKilled ? "border-destructive/60" : ""}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base font-mono">{flag.key}</CardTitle>
            {flag.description && (
              <CardDescription className="mt-1">{flag.description}</CardDescription>
            )}
            {wasAutoKilled && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                Previously launched — currently OFF. Check recent activity for the reason.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`enabled-${flag.key}`} className="text-sm">
              {flag.enabled ? "On" : "Off"}
            </Label>
            <Switch
              id={`enabled-${flag.key}`}
              checked={flag.enabled}
              onCheckedChange={(v) => onChange({ enabled: v })}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="mb-1 flex items-center justify-between">
            <Label className="text-sm">Rollout: {rollout}%</Label>
            {rollout !== flag.rollout_percentage && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onChange({ rollout_percentage: rollout })}
              >
                Save
              </Button>
            )}
          </div>
          <Slider
            value={[rollout]}
            onValueChange={([v]) => setRollout(v)}
            min={0}
            max={100}
            step={5}
            disabled={!flag.enabled}
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={flag.enabled_for_admins_only}
              onCheckedChange={(v) => onChange({ enabled_for_admins_only: v })}
            />
            Admins only
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={flag.auto_kill_enabled}
              onCheckedChange={(v) => onChange({ auto_kill_enabled: v })}
            />
            Auto-kill enabled
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateFlagDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const cleaned = key.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
      if (cleaned.length < 3) throw new Error("Key must be at least 3 characters");
      const { error } = await supabase.from("feature_flags" as never).insert({
        key: cleaned,
        description: description.trim() || null,
        enabled: false,
        rollout_percentage: 0,
        auto_kill_enabled: true,
      } as never);
      if (error) throw error;
      return cleaned;
    },
    onSuccess: (k) => {
      toast.success(`Flag ${k} created`);
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create feature flag</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="new-flag-key">Key</Label>
          <Input
            id="new-flag-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="new-checkout-flow"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Lowercase, digits, and hyphens only. Used in <code>useFeatureFlag(&quot;…&quot;)</code>.
          </p>
        </div>
        <div>
          <Label htmlFor="new-flag-desc">Description (optional)</Label>
          <Textarea
            id="new-flag-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          Create (starts OFF)
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
